import React from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Paper,
  Stack,
  Typography
} from '@mui/material';
import {
  Close as CloseIcon,
  Dns as DnsIcon,
  Inventory2 as ImageIcon,
  Info as InfoIcon,
  ViewList as ViewAllIcon,
  CheckCircle as ReadyIcon,
  ErrorOutline as NotReadyIcon,
  Description as DocIcon,
  MenuBook as ReadIcon,
  ArrowDropDown as ArrowDropDownIcon,
  PictureAsPdf as PdfIcon,
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../utils/api';
import ReleaseNotesReaderDialog from './ReleaseNotesReaderDialog';
import { exportElementToPdf } from '../utils/exportElementPdf';

const COMPONENT_COLORS = {
  Frontend: { bg: '#e0f7fa', border: '#4dd0e1', accent: '#00838f' },
  Backend: { bg: '#fff8e1', border: '#ffca28', accent: '#f57c00' },
  Database: { bg: '#e8f5e9', border: '#66bb6a', accent: '#2e7d32' }
};

/** Blinking red cue next to the dialog Close button (must close explicitly). */
const CloseWindowHint = ({ placement = 'absolute' } = {}) => (
  <Box
    sx={{
      ...(placement === 'absolute'
        ? { position: 'absolute', top: 10, right: 56, zIndex: 2 }
        : { position: 'relative', flexShrink: 0 }),
      display: 'flex',
      alignItems: 'center',
      gap: 0.75,
      maxWidth: { xs: 170, sm: 260 },
      px: 1.1,
      py: 0.55,
      borderRadius: 1,
      border: '2px solid #d32f2f',
      bgcolor: '#ffebee',
      boxShadow: '0 0 0 2px rgba(211,47,47,0.15)',
      animation: 'tenanthubCloseHintBlink 1.1s ease-in-out infinite',
      '@keyframes tenanthubCloseHintBlink': {
        '0%, 100%': { opacity: 1, transform: 'translateX(0)' },
        '50%': { opacity: 0.45, transform: 'translateX(-2px)' }
      }
    }}
  >
    <Typography
      variant="caption"
      sx={{
        color: '#b71c1c',
        fontWeight: 800,
        lineHeight: 1.25,
        fontSize: { xs: '0.65rem', sm: '0.72rem' }
      }}
    >
      Click Close to leave this window
    </Typography>
    <ArrowForwardIcon sx={{ color: '#d32f2f', fontSize: 20, flexShrink: 0 }} />
  </Box>
);

const blockDialogDismiss = (_event, reason) => {
  if (reason === 'backdropClick' || reason === 'escapeKeyDown') return false;
  return true;
};

const FALLBACK_GUIDE = {
  pattern: 'MAJOR.YYYY.WW.MMMM-XXXXXX',
  example: '1.2026.32.0005-482917',
  diagram: [
    { segment: '1', meaning: 'major — product line / breaking-change line' },
    { segment: '2026', meaning: 'year (UTC)' },
    { segment: '32', meaning: 'ISO week (01–53)' },
    { segment: '0005', meaning: '4-digit minor within the week (0001–9999)' },
    { segment: '482917', meaning: 'random 6-digit rollout suffix (unique per build/deploy)' }
  ],
  notes: [
    'VERSION files store the core only (e.g. 1.2026.32.0005).',
    'Image tags append -XXXXXX on each build/deploy.',
    'Header shows the full tag when injected from the running image env.',
    'Legacy 2-digit minors (e.g. 1.2026.32.05) still parse and pad to 0005.',
    'Lifecycle: docker-desktop-k8s-local → az-dev-01 → az-prod-01.'
  ]
};

const Mono = ({ children, sx = {} }) => (
  <Typography
    component="span"
    sx={{
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
      fontWeight: 700,
      wordBreak: 'break-all',
      ...sx
    }}
  >
    {children}
  </Typography>
);

const serviceKeyFromLabel = (label) => {
  const s = String(label || '').toLowerCase();
  if (s.startsWith('front')) return 'frontend';
  if (s.startsWith('back')) return 'backend';
  if (s.startsWith('data')) return 'database';
  return null;
};

const coreFromComponent = (component) => {
  if (!component) return null;
  return component.core_version
    || String(component.version || component.image_tag || '').replace(/-\d{6}$/, '')
    || null;
};

const ReleaseNotesPanel = ({ components, releaseNotes, onOpenNote }) => {
  const cards = (components || []).map((c) => {
    const service = serviceKeyFromLabel(c.label);
    const core = coreFromComponent(c);
    const meta = (service && releaseNotes?.[service]) || null;
    const available = Boolean(meta?.available);
    return { component: c, service, core, meta, available };
  });

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        background: 'linear-gradient(145deg, #fffbeb 0%, #f0fdf4 100%)'
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
        <DocIcon color="warning" />
        <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
          Release notes (markdown)
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Open the generated <Mono sx={{ fontWeight: 600 }}>tenanthub-release-notes-{'{version}'}.md</Mono> for each service.
      </Typography>
      <Grid container spacing={1.25}>
        {cards.map(({ component, service, core, meta, available }) => {
          const colors = COMPONENT_COLORS[component.label] || COMPONENT_COLORS.Frontend;
          return (
            <Grid item xs={12} md={4} key={component.label}>
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  height: '100%',
                  borderRadius: 2,
                  border: `1.5px solid ${colors.border}`,
                  bgcolor: colors.bg
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: colors.accent, mb: 0.75 }}>
                  {component.label}
                </Typography>
                <Typography variant="caption" display="block" sx={{ mb: 1 }}>
                  Version: <Mono>{core || '—'}</Mono>
                </Typography>
                {available ? (
                  <Button
                    fullWidth
                    size="small"
                    variant="contained"
                    startIcon={<ReadIcon />}
                    onClick={() => onOpenNote({
                      service,
                      version: meta.version || core,
                      label: component.label
                    })}
                    data-html2canvas-ignore="true"
                    sx={{ bgcolor: colors.accent, '&:hover': { bgcolor: colors.accent, filter: 'brightness(0.92)' } }}
                  >
                    Read full notes
                  </Button>
                ) : (
                  <Button fullWidth size="small" variant="outlined" disabled startIcon={<DocIcon />}>
                    Notes not found
                  </Button>
                )}
                {available && meta?.filename && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
                    {meta.filename}
                  </Typography>
                )}
              </Paper>
            </Grid>
          );
        })}
      </Grid>
    </Paper>
  );
};

const FormatGuidePanel = ({ guide }) => {
  const g = guide || FALLBACK_GUIDE;
  const example = g.example || FALLBACK_GUIDE.example;
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        background: 'linear-gradient(160deg, #f8fafc 0%, #eef6ff 55%, #f3faf4 100%)'
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
        <InfoIcon color="primary" fontSize="small" />
        <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
          Version format
        </Typography>
        <Chip size="small" label={g.pattern || FALLBACK_GUIDE.pattern} sx={{ fontWeight: 700 }} />
      </Stack>

      <Box
        sx={{
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
          fontSize: '0.92rem',
          lineHeight: 1.55,
          bgcolor: 'rgba(15, 23, 42, 0.92)',
          color: '#e2e8f0',
          borderRadius: 2,
          p: 2,
          mb: 2,
          overflowX: 'auto'
        }}
      >
        <Box sx={{ color: '#7dd3fc', fontWeight: 800, mb: 1 }}>{example}</Box>
        <Box component="pre" sx={{ m: 0, whiteSpace: 'pre', color: '#cbd5e1' }}>
{`│        │  │     └── random 6-digit (per rollout)
│        │  └── 4-digit minor (0001–9999 / week)
│        └── ISO week
└── year / major`}
        </Box>
      </Box>

      <Stack spacing={1}>
        {(g.diagram || FALLBACK_GUIDE.diagram).map((row) => (
          <Box key={row.segment} sx={{ display: 'flex', gap: 1.25, alignItems: 'baseline' }}>
            <Chip
              size="small"
              label={row.segment}
              sx={{
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                fontWeight: 800,
                minWidth: 72,
                justifyContent: 'center'
              }}
            />
            <Typography variant="body2" color="text.secondary">{row.meaning}</Typography>
          </Box>
        ))}
      </Stack>

      <Divider sx={{ my: 2 }} />
      <Stack spacing={0.75}>
        {(g.notes || FALLBACK_GUIDE.notes).map((note) => (
          <Typography key={note} variant="body2" color="text.secondary">
            • {note}
          </Typography>
        ))}
      </Stack>
    </Paper>
  );
};

const ComponentDetailCard = ({ component, selected, onReadNotes }) => {
  if (!component) return null;
  const colors = COMPONENT_COLORS[component.label] || COMPONENT_COLORS.Frontend;
  const parts = component.parts || {};
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 2,
        border: selected ? `2px solid ${colors.border}` : '1px solid',
        borderColor: selected ? colors.border : 'divider',
        bgcolor: selected ? colors.bg : 'background.paper',
        height: '100%'
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 800, color: colors.accent }}>
          {component.label}
        </Typography>
        <Chip size="small" label={component.short || component.label} color="default" />
      </Stack>

      <Stack spacing={1.1}>
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
            IMAGE
          </Typography>
          <Typography variant="body2">
            <Mono>{component.image || `${component.repository || '—'}:${component.image_tag || component.version || '—'}`}</Mono>
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
            REPOSITORY
          </Typography>
          <Typography variant="body2"><Mono>{component.repository || '—'}</Mono></Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
            IMAGE TAG / VERSION
          </Typography>
          <Typography variant="body2"><Mono>{component.image_tag || component.version || '—'}</Mono></Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
            CORE (VERSION FILE)
          </Typography>
          <Typography variant="body2"><Mono>{component.core_version || '—'}</Mono></Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip size="small" icon={<ImageIcon />} label={`source: ${component.source || 'unknown'}`} />
          {parts.suffix && <Chip size="small" color="secondary" label={`rollout: ${parts.suffix}`} />}
          {parts.isoWeek && <Chip size="small" label={`week ${parts.isoWeek}`} />}
          {parts.minor && <Chip size="small" label={`minor ${parts.minor}`} />}
        </Box>
        {component.service && (
          <Typography variant="caption" color="text.secondary">
            Service: <Mono sx={{ fontWeight: 600 }}>{component.service}</Mono>
          </Typography>
        )}
        {onReadNotes && (() => {
          const service = serviceKeyFromLabel(component.label);
          const core = coreFromComponent(component);
          if (!service || !core) return null;
          return (
            <Button
              size="small"
              variant="outlined"
              startIcon={<ReadIcon />}
                    onClick={() => onReadNotes({ service, version: core, label: component.label })}
                    data-html2canvas-ignore="true"
                    sx={{ alignSelf: 'flex-start', mt: 0.5, borderColor: colors.accent, color: colors.accent }}
            >
              Read release notes
            </Button>
          );
        })()}
      </Stack>
    </Paper>
  );
};

const RunningServicesPanel = ({ running, loading }) => {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }
  const services = running?.services || [];
  if (!running?.available) {
    return (
      <Paper elevation={0} sx={{ p: 2, border: '1px dashed', borderColor: 'divider', borderRadius: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Live cluster pods are unavailable here
          {running?.reason ? ` (${running.reason})` : ''}.
          Showing TenantHub component images from the backend env above.
        </Typography>
      </Paper>
    );
  }
  if (!services.length) {
    return (
      <Paper elevation={0} sx={{ p: 2, border: '1px dashed', borderColor: 'divider', borderRadius: 2 }}>
        <Typography variant="body2" color="text.secondary">
          No TenantHub pods discovered in reachable namespaces.
        </Typography>
      </Paper>
    );
  }
  return (
    <Stack spacing={1.5}>
      {services.map((svc) => (
        <Paper
          key={`${svc.namespace}/${svc.name}`}
          elevation={0}
          sx={{ p: 1.75, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}
        >
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap' }}>
            {svc.ready ? <ReadyIcon color="success" fontSize="small" /> : <NotReadyIcon color="warning" fontSize="small" />}
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{svc.name}</Typography>
            <Chip size="small" label={svc.namespace} />
            <Chip size="small" label={svc.phase} color={svc.ready ? 'success' : 'warning'} variant="outlined" />
          </Stack>
          <Stack spacing={0.75}>
            {(svc.containers || []).map((c) => (
              <Box key={`${svc.name}-${c.name}`} sx={{ pl: 0.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                  {c.name}
                </Typography>
                <Typography variant="body2"><Mono sx={{ fontSize: '0.8rem' }}>{c.image}</Mono></Typography>
              </Box>
            ))}
          </Stack>
        </Paper>
      ))}
    </Stack>
  );
};

const AppVersionDetailsDialog = ({
  open,
  onClose,
  initialComponent = 'Frontend',
  initialShowAll = false,
  summaryVersions = null
}) => {
  const [details, setDetails] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [showAllServices, setShowAllServices] = React.useState(false);
  const [activeKey, setActiveKey] = React.useState(initialComponent);
  const [readerOpen, setReaderOpen] = React.useState(false);
  const [readerTarget, setReaderTarget] = React.useState({ service: '', version: '', label: '' });
  const [pdfExporting, setPdfExporting] = React.useState(false);
  const pdfCaptureRef = React.useRef(null);

  React.useEffect(() => {
    if (!open) return undefined;
    setActiveKey(initialComponent || 'Frontend');
    setShowAllServices(Boolean(initialShowAll));
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await api.get('/settings/app-versions', { params: { detailed: true } });
        if (!cancelled) setDetails(res.data?.data || null);
      } catch {
        if (!cancelled) {
          setDetails(summaryVersions ? {
            ...summaryVersions,
            format_guide: FALLBACK_GUIDE,
            components: [summaryVersions.frontend, summaryVersions.backend, summaryVersions.database].filter(Boolean),
            running_services: { available: false, reason: 'api_error', services: [] }
          } : null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [open, initialComponent, initialShowAll, summaryVersions]);

  const components = details?.components || [
    details?.frontend || summaryVersions?.frontend,
    details?.backend || summaryVersions?.backend,
    details?.database || summaryVersions?.database
  ].filter(Boolean);

  const active = components.find((c) => c.label === activeKey) || components[0];
  const guide = details?.format_guide || FALLBACK_GUIDE;

  const handleExportAllServicesPdf = async () => {
    try {
      if (!showAllServices) {
        setShowAllServices(true);
        await new Promise((resolve) => setTimeout(resolve, 400));
      }
      // Extra frame so layout/gradients settle before capture
      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      if (!pdfCaptureRef.current) {
        toast.error('All-services view is not ready to export');
        return;
      }
      setPdfExporting(true);
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
      const versionHint = components.map((c) => c.core_version || c.version).filter(Boolean)[0] || 'current';
      const safeVersion = String(versionHint).replace(/[^\w.-]+/g, '_');
      const fileName = `tenanthub-all-services-${safeVersion}-${stamp}.pdf`;
      await exportElementToPdf(pdfCaptureRef.current, { fileName });
      toast.success(`PDF downloaded: ${fileName}`);
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Failed to generate PDF');
    } finally {
      setPdfExporting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (!blockDialogDismiss(event, reason)) return;
        onClose?.();
      }}
      disableEscapeKeyDown
      maxWidth="md"
      fullWidth
      scroll="paper"
      BackdropProps={{
        sx: { backgroundColor: 'rgba(15, 23, 42, 0.62)' }
      }}
    >
      <DialogTitle sx={{ pr: { xs: 7, sm: 34 }, position: 'relative' }}>
        <Typography variant="h6" sx={{ fontWeight: 800 }}>
          Running image details
        </Typography>
        <Typography variant="body2" color="text.secondary">
          TenantHub Super Admin · {guide.pattern}
        </Typography>
        <CloseWindowHint />
        <IconButton
          aria-label="Close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 12,
            top: 12,
            border: '2px solid #d32f2f',
            bgcolor: '#ffebee',
            '&:hover': { bgcolor: '#ffcdd2' }
          }}
        >
          <CloseIcon sx={{ color: '#c62828' }} />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading && !details ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={2.5}>
            <Stack direction="row" spacing={1.25} flexWrap="wrap" useFlexGap>
              {components.map((c) => {
                const colors = COMPONENT_COLORS[c.label] || COMPONENT_COLORS.Frontend;
                const selected = active?.label === c.label && !showAllServices;
                return (
                  <Box
                    key={c.label}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      minWidth: 0
                    }}
                  >
                    <Box
                      sx={{
                        height: 28,
                        display: 'flex',
                        alignItems: 'flex-end',
                        justifyContent: 'center',
                        mb: 0.25
                      }}
                    >
                      {selected ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1 }}>
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 800,
                              color: colors.accent,
                              fontSize: '0.65rem',
                              letterSpacing: 0.4,
                              textTransform: 'uppercase'
                            }}
                          >
                            Selected
                          </Typography>
                          <ArrowDropDownIcon
                            sx={{
                              color: colors.accent,
                              fontSize: 32,
                              mt: -0.75,
                              filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.15))'
                            }}
                          />
                        </Box>
                      ) : null}
                    </Box>
                    <Chip
                      clickable
                      onClick={() => { setActiveKey(c.label); setShowAllServices(false); }}
                      label={`${c.label}: ${c.version || '—'}`}
                      sx={{
                        fontWeight: 700,
                        cursor: 'pointer',
                        bgcolor: selected ? colors.bg : 'background.paper',
                        border: selected ? `2.5px solid ${colors.accent}` : `1.5px solid ${colors.border}`,
                        color: colors.accent,
                        maxWidth: '100%',
                        boxShadow: selected ? `0 0 0 3px ${colors.border}55` : 'none',
                        transform: selected ? 'translateY(-1px)' : 'none',
                        transition: 'transform 0.12s ease, box-shadow 0.12s ease, border-color 0.12s ease',
                        '&:hover': {
                          cursor: 'pointer',
                          boxShadow: `0 2px 8px ${colors.border}88`
                        },
                        '& .MuiChip-label': {
                          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                          fontSize: '0.75rem',
                          cursor: 'pointer'
                        }
                      }}
                    />
                  </Box>
                );
              })}
            </Stack>

            {!showAllServices && active && (
              <ComponentDetailCard
                component={active}
                selected
                onReadNotes={(target) => {
                  setReaderTarget(target);
                  setReaderOpen(true);
                }}
              />
            )}

            {!showAllServices && (
              <ReleaseNotesPanel
                components={components}
                releaseNotes={details?.release_notes}
                onOpenNote={(target) => {
                  setReaderTarget(target);
                  setReaderOpen(true);
                }}
              />
            )}

            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                border: '2px solid',
                borderColor: showAllServices ? 'primary.main' : 'divider',
                background: showAllServices
                  ? 'linear-gradient(135deg, rgba(25,118,210,0.08), rgba(0,150,136,0.08))'
                  : 'linear-gradient(135deg, #fff7ed, #eff6ff)',
                transition: 'border-color 0.15s ease, transform 0.15s ease'
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
                <Box
                  onClick={() => setShowAllServices((v) => !v)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    flex: 1,
                    minWidth: 220,
                    cursor: 'pointer',
                    '&:hover': { opacity: 0.92 }
                  }}
                >
                  <ViewAllIcon color="primary" />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                      View all running services
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Frontend, Backend, Database images plus live cluster pods when available
                    </Typography>
                  </Box>
                  <DnsIcon color="action" />
                </Box>
                <Button
                  data-html2canvas-ignore="true"
                  variant="contained"
                  color="error"
                  size="small"
                  startIcon={pdfExporting ? <CircularProgress size={14} color="inherit" /> : <PdfIcon />}
                  disabled={pdfExporting || loading}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleExportAllServicesPdf();
                  }}
                  sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}
                >
                  {pdfExporting ? 'Creating PDF…' : 'Download PDF'}
                </Button>
              </Stack>
            </Paper>

            <Box
              ref={pdfCaptureRef}
              sx={{
                display: showAllServices ? 'block' : 'none',
                p: 2,
                borderRadius: 2,
                bgcolor: '#ffffff',
                border: '1px solid',
                borderColor: 'divider'
              }}
            >
              <Stack spacing={2}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>
                    TenantHub — all running services
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Snapshot of Frontend, Backend, Database images and live pods
                    {details?.environment?.pod_namespace ? ` · ns ${details.environment.pod_namespace}` : ''}
                  </Typography>
                </Box>

                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                  Declared TenantHub components
                </Typography>
                <Grid container spacing={1.5}>
                  {components.map((c) => (
                    <Grid item xs={12} md={4} key={c.label}>
                      <ComponentDetailCard
                        component={c}
                        selected={c.label === activeKey}
                        onReadNotes={(target) => {
                          setReaderTarget(target);
                          setReaderOpen(true);
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>

                <ReleaseNotesPanel
                  components={components}
                  releaseNotes={details?.release_notes}
                  onOpenNote={(target) => {
                    setReaderTarget(target);
                    setReaderOpen(true);
                  }}
                />

                <Typography variant="subtitle2" sx={{ fontWeight: 800, mt: 1 }}>
                  Live pods
                </Typography>
                <RunningServicesPanel running={details?.running_services} loading={loading} />

                <FormatGuidePanel guide={guide} />

                {details?.environment && (
                  <Typography variant="caption" color="text.secondary">
                    Runtime: {details.environment.node_env || '—'}
                    {details.environment.pod_namespace ? ` · ns ${details.environment.pod_namespace}` : ''}
                    {details.environment.in_cluster ? ' · in-cluster' : ' · out-of-cluster'}
                    {' · generated '}
                    {new Date().toISOString()}
                  </Typography>
                )}
              </Stack>
            </Box>

            {!showAllServices && (
              <>
                <FormatGuidePanel guide={guide} />
                {details?.environment && (
                  <Typography variant="caption" color="text.secondary">
                    Runtime: {details.environment.node_env || '—'}
                    {details.environment.pod_namespace ? ` · ns ${details.environment.pod_namespace}` : ''}
                    {details.environment.in_cluster ? ' · in-cluster' : ' · out-of-cluster'}
                  </Typography>
                )}
              </>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          py: 1.5,
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1.25
        }}
      >
        <CloseWindowHint placement="inline" />
        <Button
          onClick={onClose}
          variant="contained"
          color="error"
          sx={{ fontWeight: 700, border: '2px solid #b71c1c' }}
        >
          Close
        </Button>
      </DialogActions>

      <ReleaseNotesReaderDialog
        open={readerOpen}
        onClose={() => setReaderOpen(false)}
        service={readerTarget.service}
        version={readerTarget.version}
        label={readerTarget.label}
      />
    </Dialog>
  );
};

export default AppVersionDetailsDialog;
